/**
 * React kontekst za scroll-spy navigaciju sekcija na HomePage (inline header / burger meni).
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  HOME_SECTION_LABELS,
  HOME_SECTION_SCROLL_OFFSET,
} from '../constants/homeSectionNavConstants';

const HomeSectionNavContext = createContext(null);

const PROGRAMMATIC_SCROLL_MS = 900;
const BOTTOM_SCROLL_THRESHOLD_PX = 64;

function isScrolledNearBottom(thresholdPx = BOTTOM_SCROLL_THRESHOLD_PX) {
  const doc = document.documentElement;
  const remaining = doc.scrollHeight - window.scrollY - window.innerHeight;
  return remaining <= thresholdPx;
}

/**
 * Provider za home sekcijsku navigaciju — registruje sekcije, prati aktivnu sekciju pri scroll-u.
 * @param {{ children: import('react').ReactNode }} props
 */
export function HomeSectionNavProvider({ children }) {
  const [sections, setSections] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [placement, setPlacement] = useState('inline');
  const isProgrammaticScrollRef = useRef(false);
  const scrollUnlockTimerRef = useRef(null);

  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);

  const resolveActiveFromScroll = useCallback(() => {
    if (!sectionIds.length) return;

    if (isScrolledNearBottom()) {
      setActiveSectionId(sectionIds[sectionIds.length - 1]);
      return;
    }

    let currentId = sectionIds[0];
    for (const id of sectionIds) {
      const el = document.getElementById(id);
      if (!el) continue;
      const { top } = el.getBoundingClientRect();
      if (top - HOME_SECTION_SCROLL_OFFSET <= 12) {
        currentId = id;
      }
    }
    setActiveSectionId(currentId);
  }, [sectionIds]);

  const scrollToSection = useCallback(
    (id) => {
      const el = document.getElementById(id);
      if (!el) return;

      isProgrammaticScrollRef.current = true;
      setActiveSectionId(id);

      const top = el.getBoundingClientRect().top + window.scrollY - HOME_SECTION_SCROLL_OFFSET;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

      if (scrollUnlockTimerRef.current) {
        window.clearTimeout(scrollUnlockTimerRef.current);
      }
      scrollUnlockTimerRef.current = window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
        resolveActiveFromScroll();
      }, PROGRAMMATIC_SCROLL_MS);
    },
    [resolveActiveFromScroll],
  );

  useEffect(() => {
    const onScroll = () => {
      if (isProgrammaticScrollRef.current) return;
      resolveActiveFromScroll();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (scrollUnlockTimerRef.current) {
        window.clearTimeout(scrollUnlockTimerRef.current);
      }
    };
  }, [resolveActiveFromScroll]);

  useEffect(() => {
    if (!sectionIds.length) {
      setActiveSectionId(null);
      return;
    }
    if (!activeSectionId || !sectionIds.includes(activeSectionId)) {
      setActiveSectionId(sectionIds[0]);
    }
  }, [activeSectionId, sectionIds]);

  const registerSections = useCallback((nextSections) => {
    setSections(Array.isArray(nextSections) ? nextSections : []);
  }, []);

  const value = useMemo(
    () => ({
      sections,
      activeSectionId,
      placement,
      scrollToSection,
      registerSections,
      setPlacement,
    }),
    [sections, activeSectionId, placement, scrollToSection, registerSections],
  );

  return (
    <HomeSectionNavContext.Provider value={value}>{children}</HomeSectionNavContext.Provider>
  );
}

/**
 * Čita home sekcijsku navigaciju iz konteksta (`sections`, `scrollToSection`, `placement`, …).
 * @returns {object|null} Vrednost konteksta ili `null` van providera.
 */
export function useHomeSectionNav() {
  return useContext(HomeSectionNavContext);
}

/**
 * Registruje vidljive home sekcije u `HomeSectionNavContext` (scroll-spy + header nav).
 * @param {string[]} visibleSectionIds - Niz DOM id-jeva sekcija koje su trenutno prikazane.
 */
export function useRegisterHomeSections(visibleSectionIds) {
  const registerSections = useHomeSectionNav()?.registerSections;

  useEffect(() => {
    if (!registerSections) return undefined;

    const items = (visibleSectionIds || [])
      .filter(Boolean)
      .map((id) => ({
        id,
        label: HOME_SECTION_LABELS[id] || id,
      }));

    registerSections(items);

    return () => {
      registerSections([]);
    };
  }, [registerSections, visibleSectionIds]);
}
